import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
    LineChart, Line, Legend,
} from "recharts";
import { Gauge, Activity, Sparkles, Coins, Clock, Recycle } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

function fmtInt(n) {
    if (n === null || n === undefined) return "—";
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
    return String(n);
}
function fmtUsd(n) {
    if (!n) return "—";
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
    return `$${n}`;
}

export default function EDIDashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/edi/summary")
            .then((r) => setData(r.data))
            .catch(() => toast.error("Failed to load EDI dashboard"));
    }, []);

    if (!data) return <div className="kicker text-slate-500">Loading Enterprise Delivery Intelligence…</div>;
    const k = data.kpis;
    const trend = data.growth_trend;

    const kpiCards = [
        { label: "Knowledge Assets",              value: fmtInt(k.knowledge_assets),        sub: "Documents indexed" },
        { label: "Engineering Reuse",             value: `${k.engineering_reuse_pct}%`,     sub: "Portfolio average" },
        { label: "Engineering Hours Saved",       value: fmtInt(k.engineering_hours_saved), sub: "Year to date" },
        { label: "Estimated Cost Saved",          value: fmtUsd(k.cost_saved_usd),          sub: "Portfolio value" },
        { label: "Reusable Projects",             value: fmtInt(k.reusable_projects),       sub: "Archived + tagged" },
        { label: "Reusable Data Sheets",          value: fmtInt(k.reusable_data_sheets),    sub: "Template-ready" },
        { label: "Drawings Reused",               value: fmtInt(k.drawings_reused),         sub: "Live reuse" },
        { label: "Knowledge Growth",              value: `${k.knowledge_growth_pct}%`,      sub: "12-month" },
        { label: "Engineering Productivity",      value: `${k.engineering_productivity_idx}×`, sub: "Baseline 1.00" },
        { label: "Document Search Time",          value: `${k.document_search_time_sec}s`,  sub: "Average" },
        { label: "Avg Bid Preparation Time",      value: `${k.avg_bid_prep_days} d`,        sub: "Portfolio" },
        { label: "Procurement Cycle Time",        value: `${k.procurement_cycle_days} d`,   sub: "Portfolio" },
        { label: "Contract Risk Index",           value: k.contract_risk_index.toFixed(2),  sub: "Lower is better" },
        { label: "Lessons Learned Captured",      value: fmtInt(k.lessons_learned_captured),sub: "Archive" },
        { label: "Engineering Rework Reduction",  value: `${k.rework_reduction_pct}%`,      sub: "vs baseline" },
        { label: "Knowledge Utilisation",         value: `${k.knowledge_utilisation_pct}%`, sub: "Active users" },
    ];

    return (
        <div className="space-y-8" data-testid={EDI.dashboardRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        EDI Executive Dashboard
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Sixteen executive KPIs measuring how effectively SGS Bateman is converting
                        engineering knowledge into speed, quality and commercial confidence.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Live · GPT-5.2 grounded</span>
                </div>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {kpiCards.map((c) => (
                    <div
                        key={c.label}
                        data-testid={EDI.kpi(c.label.toLowerCase().replace(/\W+/g, "-"))}
                        className="glass hover-lift rounded-xl p-5 relative overflow-hidden"
                    >
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <div className="kicker text-slate-400 text-[10px]">{c.label}</div>
                        <div className="mt-3 font-display font-black text-white text-2xl tracking-tighter">{c.value}</div>
                        <div className="mt-1 text-[11px] text-slate-500 font-body">{c.sub}</div>
                    </div>
                ))}
            </div>

            {/* Knowledge growth trend */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl p-6 xl:col-span-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="kicker text-slate-400">Knowledge Growth</div>
                            <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">
                                Documents · Reuse % · Hours Saved
                            </h3>
                        </div>
                        <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-body">
                            <Activity className="h-3 w-3" strokeWidth={2} />
                            <span>Compounding growth</span>
                        </div>
                    </div>
                    <div className="h-[320px] mt-5 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={trend}>
                                <defs>
                                    <linearGradient id="g-docs" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.55} />
                                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="g-hrs" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.45} />
                                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="quarter" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip
                                    contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }}
                                    labelStyle={{ color: "#94A3B8", textTransform: "uppercase", fontSize: 11, letterSpacing: 2 }}
                                />
                                <Area dataKey="docs" name="Documents"    stroke="#D4AF37" strokeWidth={2.4} fill="url(#g-docs)" />
                                <Area dataKey="hours_saved" name="Hours Saved" stroke="#3B82F6" strokeWidth={1.6} fill="url(#g-hrs)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass rounded-xl p-6">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <Recycle className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Reuse %</span>
                    </div>
                    <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">
                        Portfolio reuse velocity
                    </h3>
                    <div className="h-[260px] mt-4 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trend}>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="quarter" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                <Line dataKey="reuse_pct" stroke="#10B981" strokeWidth={2.4} dot={{ r: 3, fill: "#10B981" }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-3 text-xs text-slate-500 font-body">
                        Reuse rate has grown from 12.1% (Q1 &apos;24) to 47.3% (Q1 &apos;26) — a 3.9× improvement
                        driven by the Knowledge Reuse and Intelligent Templates capabilities.
                    </div>
                </div>
            </div>

            {/* Efficiency stripe */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="glass rounded-xl p-5 flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-[color:var(--sgsb-gold)]/10 grid place-items-center">
                        <Clock className="h-5 w-5 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="kicker text-slate-400 text-[10px]">Bid Preparation</div>
                        <div className="font-display font-black text-white text-2xl tracking-tighter">11.4 days</div>
                        <div className="text-[11px] text-emerald-400 font-body">-52.5% vs baseline</div>
                    </div>
                </div>
                <div className="glass rounded-xl p-5 flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-[color:var(--sgsb-gold)]/10 grid place-items-center">
                        <Coins className="h-5 w-5 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="kicker text-slate-400 text-[10px]">Estimated Financial Impact</div>
                        <div className="font-display font-black text-white text-2xl tracking-tighter">$32.76M</div>
                        <div className="text-[11px] text-emerald-400 font-body">Portfolio, YTD</div>
                    </div>
                </div>
                <div className="glass rounded-xl p-5 flex items-start gap-3">
                    <div className="h-10 w-10 rounded-md border border-[color:var(--sgsb-gold)]/40 bg-[color:var(--sgsb-gold)]/10 grid place-items-center">
                        <Gauge className="h-5 w-5 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    </div>
                    <div>
                        <div className="kicker text-slate-400 text-[10px]">Engineering Productivity</div>
                        <div className="font-display font-black text-white text-2xl tracking-tighter">1.34×</div>
                        <div className="text-[11px] text-emerald-400 font-body">Baseline 1.00</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
